import type {MutableRefObject} from 'react'
import type {UseFormReturn} from 'react-hook-form'

import {useFeaturesState} from '../../../../../Organisms/Features'
import type {BulkOrderFormValues} from '../../../../BulkOrdersFormValues'
import {ShippingType} from '../../../../declarations/OrderIntake.enums'
import type {OrderIntakeMaterialOptionPayload} from '../../../../declarations/types'
import type {MaterialOrderForm} from '../../MaterialOrderEditor/types'
import {useSlotRelease} from './useSlotRelease'
import {useResetSlotSiloConfig} from './useResetSlotSiloConfig'

interface UseResetDeliveryDateTimeParams {
  index: number
  shippingType: ShippingType
  parentForm: UseFormReturn<BulkOrderFormValues>
  methods: UseFormReturn<MaterialOrderForm>
  setActiveSelectedDeliveryDate: (date: string) => void
  isDateClearedByMaterialChangeRef?: MutableRefObject<boolean>
  defaultMaterialOption: OrderIntakeMaterialOptionPayload
}

export const useResetDeliveryDateTime = ({
  index,
  shippingType,
  parentForm,
  methods,
  setActiveSelectedDeliveryDate,
  isDateClearedByMaterialChangeRef,
  defaultMaterialOption
}: UseResetDeliveryDateTimeParams) => {
  const {getFeature} = useFeaturesState()
  const {releaseSlot} = useSlotRelease()
  const {resetSlotSiloConfig} = useResetSlotSiloConfig()

  /**
   * Resets delivery date/time fields and releases the slot.
   * @param hasChangedPlant - If true, indicates the plant was changed.
   */
  const resetDeliveryDateTime = (hasChangedPlant = false) => {
    // Reset delivery date/time for Italy slot management
    const isSlotManagedCollect =
      shippingType === ShippingType.COLLECT &&
      getFeature('orderIntakeSlotManagedPlantLaneAvailability')

    if (!isSlotManagedCollect) return

    const {dirtyFields} = methods.formState
    const hasMaterialChanged =
      dirtyFields.details?.selectedMaterialOption ||
      dirtyFields.details?.selectedSplitLoadMaterialsOptions

    if (hasMaterialChanged || hasChangedPlant) {
      // Set flag to prevent the deliveryDate useEffect from resetting the material selection
      if (isDateClearedByMaterialChangeRef) {
        isDateClearedByMaterialChangeRef.current = true
      }

      // Reset delivery date/time in parent form
      parentForm.setValue(`orders.${index}.payload.deliveryDate`, '', {shouldDirty: true})
      parentForm.setValue(`orders.${index}.payload.deliveryTime.earliest`, '', {shouldDirty: true})
      parentForm.setValue(`orders.${index}.payload.deliveryTime.latest`, '', {shouldDirty: true})

      // Reset delivery date/time in child form
      methods.setValue('dateTime.deliveryDate', '', {shouldDirty: true})
      methods.setValue('dateTime.deliveryTime.earliest', '', {shouldDirty: true})
      methods.setValue('dateTime.deliveryTime.latest', '', {shouldDirty: true})
      setActiveSelectedDeliveryDate('')

      // release slot after resetting fields when user changes material or plant
      const previousSlot = parentForm.getValues().orders[index].payload.slot
      const slotExternalReference = previousSlot.externalReference
      const selectedMaterial =
        parentForm.watch(`selectedMaterial.${index}`) ?? defaultMaterialOption
      releaseSlot({
        externalReference: slotExternalReference,
        plantId: previousSlot.plantId ?? selectedMaterial.plant.plantNumber,
        materialNumber: previousSlot.materialNumber ?? selectedMaterial.material.materialNumber,
        onSuccess: () => {
          resetSlotSiloConfig(parentForm, index)
        }
      })
    }
  }

  return {resetDeliveryDateTime}
}
